import { createContext, useContext, useState } from "react";
import { api, formatApiError, getUTM, track } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, CheckCircle2, PhoneCall } from "lucide-react";

const LeadCtx = createContext(null);
export const useLead = () => useContext(LeadCtx);

const CATEGORIES = ["Retail", "Restaurant", "Salon & Beauty", "Healthcare", "Education", "Real Estate", "Fashion", "Manufacturing", "Professional Services", "D2C", "Local Business", "Startup", "Other"];

export function LeadProvider({ children }) {
    const [open, setOpen] = useState(false);
    const [prefillService, setPrefillService] = useState("");
    const [done, setDone] = useState(null);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({});

    const openLead = (service = "") => { setPrefillService(service); setForm({ interested_service: service }); setDone(null); setOpen(true); track("cta_click", { cta: "talk_to_expert", service }); };
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.mobile) { toast.error("Name and mobile are required"); return; }
        setLoading(true);
        track("lead_submitted", { source: "talk_to_expert" });
        try {
            const payload = { ...form, interested_service: form.interested_service || prefillService, utm: getUTM() };
            if (!payload.email) delete payload.email;
            const { data } = await api.post("/leads", payload);
            setDone(data.lead_id);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    return (
        <LeadCtx.Provider value={{ openLead }}>
            {children}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="lead-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                            <PhoneCall className="w-5 h-5 text-teal-700" /> Talk to a Growth Expert
                        </DialogTitle>
                    </DialogHeader>
                    {done ? (
                        <div className="text-center py-6" data-testid="lead-success">
                            <CheckCircle2 className="w-14 h-14 text-growth mx-auto mb-3" />
                            <h3 className="text-xl font-bold">Your request has been submitted successfully.</h3>
                            <p className="text-muted-foreground mt-2">Our Business Development Executive will contact you shortly. Reference: <span className="font-mono font-semibold">{done}</span></p>
                            <button className="btn-gold mt-5 px-6 py-2.5 rounded-full" onClick={() => setOpen(false)} data-testid="lead-close-btn">Done</button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Name *"><Input data-testid="lead-name" value={form.name || ""} onChange={set("name")} placeholder="Your name" /></Field>
                                <Field label="Business Name"><Input data-testid="lead-business" value={form.business_name || ""} onChange={set("business_name")} placeholder="Business" /></Field>
                                <Field label="Mobile *"><Input data-testid="lead-mobile" value={form.mobile || ""} onChange={set("mobile")} placeholder="Mobile" /></Field>
                                <Field label="WhatsApp"><Input data-testid="lead-whatsapp" value={form.whatsapp || ""} onChange={set("whatsapp")} placeholder="WhatsApp" /></Field>
                                <Field label="Email"><Input data-testid="lead-email" value={form.email || ""} onChange={set("email")} placeholder="Email" /></Field>
                                <Field label="City"><Input data-testid="lead-city" value={form.city || ""} onChange={set("city")} placeholder="City" /></Field>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Business Category">
                                    <select data-testid="lead-category" className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm" value={form.business_category || ""} onChange={set("business_category")}>
                                        <option value="">Select</option>
                                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </Field>
                                <Field label="Interested Service"><Input data-testid="lead-service" value={form.interested_service || ""} onChange={set("interested_service")} placeholder="Service" /></Field>
                                <Field label="Approx Budget"><Input data-testid="lead-budget" value={form.budget || ""} onChange={set("budget")} placeholder="₹" /></Field>
                                <Field label="Website"><Input data-testid="lead-website" value={form.website || ""} onChange={set("website")} placeholder="URL" /></Field>
                            </div>
                            <Field label="Message"><Textarea data-testid="lead-message" value={form.message || ""} onChange={set("message")} placeholder="Tell us what you need" rows={3} /></Field>
                            <button type="submit" disabled={loading} className="btn-gold w-full py-3 rounded-full flex items-center justify-center gap-2" data-testid="lead-submit-btn">
                                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Request a Callback
                            </button>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </LeadCtx.Provider>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">{label}</Label>
            {children}
        </div>
    );
}
